import React, { useEffect, useState } from "react";
import AnalyticsSection from "./AnalyticsSection";
import PanelHeadline, { Fig } from "./PanelHeadline";
import { fetchDepartures } from "../services/analyticsService";

/**
 * People who left, or were removed.
 *
 * 🔴 THIS IS NOT A FUNNEL AND IS NEVER DRAWN AS ONE. No bars, no arrows, no
 * conversion percentages between rows. The funnel above counts one population
 * so that the gap between two of its rows means something; these people are not
 * a gap in it.
 *
 * WHY THEY ARE NOT ON THE FUNNEL. Measured 2026-09-18: of the departures still
 * readable, every single one reached photos, passed the face check and unlocked
 * the Gatekeeper, and all but one were introduced to somebody. They completed
 * everything the product asks and left afterwards. On a funnel row the one
 * group with a 100% completion rate would render as the largest drop, which
 * would send an operator to fix a step that is working.
 *
 * WHY THIS PANEL HAD TO SHIP WITH THAT CHANGE. Taking them off the funnel took
 * them off every page anyone reads, which turns a correction into a
 * disappearance. Churn is not less important than drop-off; it just is not the
 * same question and needed its own home.
 */
const DeparturesBlock = ({ startLoading, stopLoading }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        startLoading();
        setFailed(false);
        const res = await fetchDepartures();
        setData(res?.data?.data || null);
      } catch (err) {
        console.error("Failed to load departures", err);
        setFailed(true);
      } finally {
        stopLoading();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const far = data?.howFarTheyGot;
  const reasons = data?.reasons;

  const Row = ({ label, value, note, tone }) => (
    <div
      className="d-flex align-items-start justify-content-between gap-3 py-12"
      style={{ borderBottom: "1px solid var(--border-color)" }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <span className="fw-semibold">{label}</span>
        {note && <div className="text-sm text-secondary-light mt-1">{note}</div>}
      </div>
      <span
        className={"fw-semibold flex-shrink-0 " + (tone === "muted" ? "text-secondary-light" : "")}
        style={{ fontVariantNumeric: "tabular-nums", fontSize: "1.05rem" }}
      >
        {value}
      </span>
    </div>
  );

  return (
    <AnalyticsSection
      id="departures"
      icon="mdi:account-arrow-right-outline"
      title="People who left"
      basis="Not a funnel step and not a drop-off. These members are gone, so they are counted separately from every number above. All time."
    >
      <PanelHeadline
        failed={failed}
        failedText="Could not load departures. This is not zero people leaving."
        empty={!data}
        emptyText="Nobody has left yet."
      >
        {far?.readable > 0 ? (
          <>
            <Fig>{data?.gone ?? 0}</Fig> accounts have gone. Of the{" "}
            <Fig>{far.readable}</Fig> we can still read,{" "}
            <Fig>{far.gatekeeper}</Fig> had finished onboarding and{" "}
            <Fig>{far.introduced}</Fig> had been introduced to somebody before
            they left.
          </>
        ) : (
          <>
            <Fig>{data?.gone ?? 0}</Fig> accounts have gone.
          </>
        )}
      </PanelHeadline>

      {data && (
        <>
          <p
            className="text-uppercase text-secondary-light mb-2 mt-3"
            style={{ fontSize: 11, letterSpacing: "0.06em" }}
          >
            Who
          </p>
          <Row
            label="Still on file"
            note={
              data.soft?.byStatus?.length
                ? data.soft.byStatus.map((s) => `${s.count} ${s.status}`).join(", ")
                : null
            }
            value={data.soft?.count ?? 0}
          />
          <Row
            label="Erased outright"
            note="No record remains, so nothing about them can be read again. Includes pre-launch accounts that were never real members."
            value={data.hard?.count ?? 0}
          />

          <p
            className="text-uppercase text-secondary-light mb-2 mt-3"
            style={{ fontSize: 11, letterSpacing: "0.06em" }}
          >
            Why
          </p>
          <Row label="Removed by us" note="Moderation" value={reasons?.removedByUs ?? 0} />
          <Row label="Left themselves" value={reasons?.leftThemselves ?? 0} />
          {/* 🔴 ALWAYS SHOWN, NEVER DROPPED. Without this row the two reasons
              above read as the whole story, and they are not close to it. */}
          <Row
            label="No reason recorded"
            note="Mostly the erased accounts, whose logs went with them."
            value={reasons?.notRecorded ?? 0}
            tone="muted"
          />

          {far?.readable > 0 && (
            <>
              <p
                className="text-uppercase text-secondary-light mb-2 mt-3"
                style={{ fontSize: 11, letterSpacing: "0.06em" }}
              >
                How far they got, of the {far.readable} still readable
              </p>
              <Row label="Added photos" value={`${far.photos} of ${far.readable}`} />
              <Row label="Passed the face check" value={`${far.faceVerified} of ${far.readable}`} />
              <Row label="Unlocked the Gatekeeper" value={`${far.gatekeeper} of ${far.readable}`} />
              <Row label="Introduced to somebody" value={`${far.introduced} of ${far.readable}`} />
              <p className="text-sm text-secondary-light mt-3 mb-0" style={{ maxWidth: "68ch" }}>
                This is the sharpest number on the page. These members did not fall out of
                onboarding, they completed it and left afterwards, so nothing in the funnel
                above would ever show them.
              </p>
            </>
          )}

          <p className="text-sm text-secondary-light mt-3 mb-0" style={{ maxWidth: "68ch" }}>
            Accounts we can evidence ever existing: <b>{data.everExisted}</b> ({data.alive} here
            today plus {data.gone} gone). Do not quote that as sign-ups. Separately we hold{" "}
            {data.signupRowsHeld} sign-up records, which is a count of a log event rather than of
            people: it misses members who joined before that logging began, and most of the erased
            accounts cannot be followed to any step.
          </p>
        </>
      )}
    </AnalyticsSection>
  );
};

export default DeparturesBlock;
